import {
  getCalendarLimitInterstitialFatigueDays,
  getCalendarReviewInterstitialFatigueDays,
  getGoogleConnectInactiveInterstitialFatigueDays,
  HelpRoutes,
} from "src/services/Config.js";
import { GOOGLE_CONNECT_STATES } from "src/utils/googleConnect.js";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Names the app-root interstitials answer to, so no call site spells its own. */
export const INTERSTITIAL_KEYS = {
  CALENDAR_LIMIT: "calendar-limit",
  GOOGLE_CONNECT_INACTIVE: "google-connect-inactive",
  CALENDAR_REVIEW: "calendar-review",
};

/** App-root interstitials, in the order they get the one slot per load. */
export const INTERSTITIAL_PRIORITY = [
  INTERSTITIAL_KEYS.CALENDAR_LIMIT,
  INTERSTITIAL_KEYS.GOOGLE_CONNECT_INACTIVE,
  INTERSTITIAL_KEYS.CALENDAR_REVIEW,
];

// The review nudge decides asynchronously, so it has no entry: nothing ranks
// below it and it only ever asks whether something above it is due.
const INTERSTITIAL_WILL_SHOW = {
  [INTERSTITIAL_KEYS.CALENDAR_LIMIT]: calendarLimitInterstitialWillShow,
  [INTERSTITIAL_KEYS.GOOGLE_CONNECT_INACTIVE]: googleConnectInactiveInterstitialWillShow,
};

/** Task-flow surfaces an app-root interstitial must never interrupt. */
export const SUPPRESSED_ROUTE_PREFIXES = [
  // Every add-calendar / sync / trial step lives in HelpRoutes, so derive those
  // rather than restating them: a new step route is suppressed automatically.
  ...new Set(
    Object.values(HelpRoutes.pageRoutes)
      .map((route) => route.split("/")[1])
      .filter(Boolean)
      .map((segment) => `/${segment}`)
  ),
  // Never open during authentication: the user lands on /signin then gets
  // routed to their destination, and opening on that transient page burns the
  // slot (the dialog mounts instantly once its chunk is cached) before the
  // post-sign-in navigation, so it never shows on the real landing page.
  "/signin",
  "/subscribe",
  "/upgrade",
  "/personalize",
  "/personalization",
  "/email-subscribe",
  "/import",
  "/preview",
  // The elections flow has its own add path; don't stack a review nudge for
  // another calendar on top of it.
  "/us-elections",
];

export const CALENDAR_LIMIT_SHOWN_KEY = "calendarLimitInterstitialShownAt";
export const CALENDAR_LIMIT_DEFAULT_FATIGUE_DAYS = 7;
export const CALENDAR_REVIEW_SHOWN_KEY = "calendarReviewInterstitialShownAt";
// One day: a user who follows many calendars has many to rate, so prompt daily
// (each load features a different unreviewed one) rather than every fortnight.
export const CALENDAR_REVIEW_DEFAULT_FATIGUE_DAYS = 1;
export const CALENDAR_REVIEW_CHECKED_KEY = "calendarReviewInterstitialCheckedAt";
export const CALENDAR_REVIEW_CHECKED_WINDOW_MS = DAY_MS;
export const GOOGLE_CONNECT_INACTIVE_SHOWN_KEY =
  "googleConnectInactiveInterstitialShownAt";
// A reload resets the per-load guard, so a zero-day window would re-open this on
// every page load rather than returning on the next sign-in.
export const GOOGLE_CONNECT_INACTIVE_DEFAULT_FATIGUE_DAYS = 1;

/** Per-user so a shared browser prompts each account, not just the first. */
export const calendarReviewShownKey = (userId) =>
  `${CALENDAR_REVIEW_SHOWN_KEY}:${userId}`;

/** A null review-prompt response is user-specific, unlike browser-wide fatigue. */
export const calendarReviewCheckedKey = (userId) =>
  `${CALENDAR_REVIEW_CHECKED_KEY}:${userId}`;

/** Per-user so a shared browser prompts each lapsed account, not just the first. */
export const googleConnectInactiveShownKey = (userId) =>
  `${GOOGLE_CONNECT_INACTIVE_SHOWN_KEY}:${userId}`;

/** Days between re-shows to the same browser; 0 always shows. */
export function resolveFatigueDays(raw, defaultDays) {
  // Number("") is 0, which would silently disable fatigue on a blanked env var.
  if (raw == null || String(raw).trim() === "") return defaultDays;
  const days = Number(raw);
  return Number.isFinite(days) && days >= 0 ? days : defaultDays;
}

/**
 * Whether a route may host an interstitial. Prefixes match on a path boundary
 * because calendar pages are "/:handle" and a bare startsWith would suppress
 * every handle beginning with one ("/connecticutsun" vs "/connect").
 * `?writeReview=1` opens the WriteReview dialog, so an interstitial there would
 * stack a second modal on top of it.
 *
 * @param {{ pathname: string, search?: string }} location
 * @returns {boolean} True when an interstitial must stay closed.
 */
export function isSuppressedRoute({ pathname, search = "" }) {
  if (new URLSearchParams(search).has("writeReview")) return true;
  return SUPPRESSED_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

/**
 * Records that an interstitial was shown (or checked) just now.
 *
 * @param {string} storageKey - localStorage key to stamp.
 */
export function stampNow(storageKey) {
  try {
    window.localStorage.setItem(storageKey, String(Date.now()));
  } catch {
    // localStorage unavailable (private mode) — the caller's per-load ref still caps it.
  }
}

/**
 * Whether `storageKey` carries no stamp inside `windowMs`. A non-positive
 * window always reports true, which is how a fatigue of 0 means "always show".
 *
 * @param {string} storageKey
 * @param {number} windowMs
 * @returns {boolean}
 */
export function notStampedWithin(storageKey, windowMs) {
  if (windowMs <= 0) return true;
  try {
    const last = Number(window.localStorage.getItem(storageKey));
    return !last || Date.now() - last > windowMs;
  } catch {
    return true;
  }
}

/** True when the upgrade interstitial is outside its fatigue window. */
export function calendarLimitNotShownRecently() {
  const fatigueMs =
    resolveFatigueDays(
      getCalendarLimitInterstitialFatigueDays(),
      CALENDAR_LIMIT_DEFAULT_FATIGUE_DAYS
    ) * DAY_MS;
  return notStampedWithin(CALENDAR_LIMIT_SHOWN_KEY, fatigueMs);
}

/** True when the review interstitial is outside its fatigue window for this user. */
export function calendarReviewNotShownRecently(userId) {
  const fatigueMs =
    resolveFatigueDays(
      getCalendarReviewInterstitialFatigueDays(),
      CALENDAR_REVIEW_DEFAULT_FATIGUE_DAYS
    ) * DAY_MS;
  return notStampedWithin(calendarReviewShownKey(userId), fatigueMs);
}

/**
 * True when the review-prompt endpoint has not answered "nothing to review"
 * recently for this user. Stamped separately from the shown key so a user with
 * nothing left to rate stops re-querying on every page load without spending
 * the longer fatigue window they would need once something becomes reviewable.
 */
export function calendarReviewNotCheckedRecently(userId) {
  return notStampedWithin(
    calendarReviewCheckedKey(userId),
    CALENDAR_REVIEW_CHECKED_WINDOW_MS
  );
}

/** True when the Google Connect interstitial is outside its fatigue window for this user. */
export function googleConnectInactiveNotShownRecently(userId) {
  const fatigueMs =
    resolveFatigueDays(
      getGoogleConnectInactiveInterstitialFatigueDays(),
      GOOGLE_CONNECT_INACTIVE_DEFAULT_FATIGUE_DAYS
    ) * DAY_MS;
  return notStampedWithin(googleConnectInactiveShownKey(userId), fatigueMs);
}

/**
 * Whether the Google Connect interstitial is due to open this load: a lapsed
 * user who still holds a grant, outside its fatigue window. The other
 * interstitials read this to yield the slot.
 *
 * @param {object} user
 * @returns {boolean}
 */
export function googleConnectInactiveInterstitialWillShow(user) {
  return (
    !!user &&
    user.googleConnectState === GOOGLE_CONNECT_STATES.INACTIVE &&
    googleConnectInactiveNotShownRecently(user._id)
  );
}

/**
 * Whether the upgrade interstitial is due to open this load: an over-cap free
 * user outside its fatigue window. The review interstitial reads this to yield
 * the slot when the upgrade nudge is the one showing.
 *
 * @param {object} user
 * @returns {boolean}
 */
export function calendarLimitInterstitialWillShow(user) {
  return (
    !!user &&
    !!user.needsConsumerCalendarUpgrade &&
    calendarLimitNotShownRecently()
  );
}

/**
 * Whether an interstitial ranked above this one is due on this load, and should
 * take the slot.
 *
 * @param {string} key - One of INTERSTITIAL_PRIORITY.
 * @param {object} user
 * @returns {boolean}
 */
export function outrankedInterstitial(key, user) {
  const rank = INTERSTITIAL_PRIORITY.indexOf(key);
  if (rank < 1) return false;
  return INTERSTITIAL_PRIORITY.slice(0, rank).some((ahead) =>
    Boolean(INTERSTITIAL_WILL_SHOW[ahead]?.(user))
  );
}

/**
 * Dialog props that render an interstitial as a mobile tray and a desktop card.
 *
 * Keyed on isMediumScreen (<960px), not the isSmallScreen (<600px) sibling
 * dialogs use, so the tray matches the app shell's own breakpoint.
 *
 * @param {boolean} isMediumScreen
 * @returns {object} Props to spread onto BottomDialog or CenterDialog.
 */
export function interstitialTrayProps(isMediumScreen) {
  if (!isMediumScreen) return { maxWidth: "xs", fullWidth: true };
  return {
    bubbleStyle: true,
    hideRectangle: true,
    sx: {
      ".MuiDialogTitle-root": { p: 0 },
      ".MuiPaper-root": {
        // BottomDialog passes fullWidth, but MUI's default maxWidth="sm" caps
        // the paper at 600px and gutters the tray on wider viewports.
        maxWidth: "100%",
        // Emotion emits nested at-rules after a selector's plain declarations,
        // so BottomDialog's own @supports height cap only loses to an override
        // written inside the same @supports keys.
        "@supports (height: 100dvh)": { maxHeight: "80dvh" },
        "@supports not (height: 100dvh)": { maxHeight: "80vh" },
        // Landscape phones: 80dvh leaves too little room for the CTA.
        "@media (max-height: 520px)": { maxHeight: "94dvh" },
      },
    },
  };
}
